import React, { FC } from 'react';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { Tile } from 'components/commercetools-ui/organisms/header/types';
import useClassNames from 'helpers/hooks/useClassNames';
import HeaderDropdownTile from './header-menu-tile';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import Button from 'components/commercetools-ui/atoms/button';
import { PlusIcon } from '@heroicons/react/24/solid';
import { NationalBook, NationalSemibold, Tiempos } from 'fonts';
import { useParams } from 'next/navigation';

export interface Props {
  show?: boolean;
  links: any[];
  tileContent?: Tile;
  onClick?: () => void;
}

const COLUMN_SPANS: Record<string, string> = {
  'Spares': 'col-span-6',
  'About Us': 'col-span-4',
  'Support': 'col-span-4',
  'Our Other Brands': 'col-span-4',
};

const COLUMN_FALLBACKS: Record<number, string> = {
  1: 'col-span-2',
  2: 'col-span-4',
  4: 'col-span-4',
  6: 'col-span-6 w-[27rem] xl:w-[33rem] 3xl:w-[43rem]',
};

const GRID_LAYOUTS: Record<string, string> = {
  'Spares': 'grid-cols-1',
  'About Us': 'grid-cols-2 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30 ',
  'Support': 'grid-cols-2 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30 ',
  'Our Other Brands': 'grid-cols-1 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30',
  'Tapware & Accessories': 'grid-cols-3 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30',
  'Collections': 'grid-cols-3 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30',
  'Showers': 'grid-cols-3 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30',
};

const MARKDOWN_STYLES: Record<string, string> = {
  'Spares':
    'font-normal text-black md:text-14 2xl:text-18 [&>img]:h-[4.25rem] [&>img]:w-[7.5rem] xl:[&>img]:h-[6.25rem] xl:[&>img]:w-[9.5rem] 3xl:[&>img]:h-[8.25rem] 3xl:[&>img]:w-[12.5rem]',
  'About Us':
    'font-normal text-black md:text-14 2xl:text-18 [&>img]:h-[4.25rem] [&>img]:w-[7.5rem] xl:[&>img]:h-[6.25rem] xl:[&>img]:w-[9.5rem] 3xl:[&>img]:h-[8.25rem] 3xl:[&>img]:w-[12.5rem]',
  'Support':
    'font-normal text-black md:text-14 2xl:text-18 [&>img]:h-[4.25rem] [&>img]:w-[7.5rem] xl:[&>img]:h-[6.25rem] xl:[&>img]:w-[9.5rem] 3xl:[&>img]:h-[8.25rem] 3xl:[&>img]:w-[12.5rem]',
  'Our Other Brands':
    'font-normal text-black md:text-14 2xl:text-18 [&>img]:h-[4.25rem] [&>img]:w-[7.5rem] xl:[&>img]:h-[6.25rem] xl:[&>img]:w-[9.5rem] 3xl:[&>img]:h-[8.25rem] 3xl:[&>img]:w-[12.5rem]',
  'Experience Centres':
    'font-normal text-black md:text-14 [&>img]:h-[10.5rem] [&>img]:w-[10rem] xl:[&>img]:h-[13.25rem] xl:[&>img]:w-[14rem] 3xl:[&>img]:h-[17.5rem] 3xl:[&>img]:w-[18rem]',
  'Collections':
    'font-normal text-black md:text-20 2xl:text-18 [&>img]:h-[4.25rem] [&>img]:w-[7.5rem] xl:[&>img]:h-[6.25rem] xl:[&>img]:w-[9.5rem] 3xl:[&>img]:h-[8.25rem] 3xl:[&>img]:w-[12.5rem]',
};

const FALLBACK_MARKDOWN_STYLE =
  'font-normal text-black md:text-14 2xl:text-18 [&>img]:h-[4.25rem] [&>img]:w-[7.5rem] xl:[&>img]:h-[6.25rem] xl:[&>img]:w-[9.5rem] 3xl:[&>img]:h-[8.25rem] 3xl:[&>img]:w-[12.5rem]';

const getColumnClass = (link: any, locale: string): string => {
  if (locale?.toLowerCase() === 'nz' && link?.title === 'Support') {
    return 'col-span-2';
  }
  if (COLUMN_SPANS[link?.title]) return COLUMN_SPANS[link.title];
  return COLUMN_FALLBACKS[link?.mega_menu?.length] || 'col-span-10 xl:col-span-8';
};

const getGridClass = (link: any, locale: string): string => {
  if (locale?.toLowerCase() === 'nz' && link?.title === 'Support') {
    return 'grid-cols-1 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30 ';
  }
  return GRID_LAYOUTS[link?.title] || getFallbackGrid(link);
};

const getFallbackGrid = (link: any): string => {
  const cols = link?.mega_menu?.length;
  if (cols === 1) return 'grid-cols-1 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30';
  if (cols === 2 || cols === 4)
    return 'grid-cols-2 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30';
  return 'grid-cols-3 gap-x-24 gap-y-16 2xl:gap-x-36 2xl:gap-y-30';
};

const getMarkdownClass = (link: any): string => {
  return (
    MARKDOWN_STYLES[link?.title] ||
    (link?.mega_menu?.length && FALLBACK_MARKDOWN_STYLE) ||
    'font-normal text-black md:text-14 2xl:text-18'
  );
};

const MenuDropdown: FC<Props> = ({ show, links, tileContent }) => {
  const { locale } = useParams();
  const wrapperClassNames = useClassNames([
    'transition absolute overflow-y-auto flex justify-between bottom-0 left-0 duration-300 ease-in-out min-h-[20rem] 2xl:min-h-[30rem] w-full translate-y-full',
    show ? 'opacity-1 z-20 delay-300' : 'opacity-0 z-[-999] pointer-events-none',
    'bg-[#ededed] py-40 px-48 xl:p-60 2xl:p-80',
  ]);

  const linksClassNames = useClassNames([
    'grid gap-x-64 gap-y-28 grid-cols-12',
    tileContent ? 'pr-116' : '',
  ]);

  return (
    <div className={wrapperClassNames}>
      <div className={linksClassNames}>
        {links?.map((link) => (
          <div key={link?.label} className={getColumnClass(link, locale as string)}>
            <div className="w-min pb-16 2xl:pb-26">
              <Typography
                className={`whitespace-nowrap text-[#2B2C39] md:text-18 2xl:text-22 ${Tiempos.className}`}
              >
                {link?.title}
              </Typography>
            </div>
            <div
              className={`grid ${getGridClass(link, locale)} ${NationalBook.className} place-items-stretch`}
            >
              {link?.mega_menu?.map((field: any, idx: number) => {
                const baseClass = `${NationalBook.className} flex ${
                  link?.title === '' ? 'flex-col' : 'flex-col-reverse'
                } ${getMarkdownClass(link)} [&>div>img]:mb-8 [&>div>img]:w-full [&>div>img]:object-cover [&>figure>div>a>img]:mb-8 [&>figure>div>a>img]:object-cover [&>img]:mb-8 [&>img]:object-cover`;

                if (!field?.mega_menu_url?.href) {
                  return (
                    <Markdown key={idx} markdown={field?.mega_menu_content} className={baseClass} />
                  );
                }

                return (
                  <Link
                    key={field?.categoryId || idx}
                    link={field?.mega_menu_url?.href}
                    target={field?.mega_menu_url?.href?.includes('https://') ? '_blank' : ''}
                  >
                    <Markdown markdown={field?.mega_menu_content} className={baseClass} />
                  </Link>
                );
              })}
            </div>
            {link?.ctas?.map((cta: any) => (
              <Link
                key={cta?.button?.title}
                link={cta?.button?.href}
                target={cta?.button?.href?.includes('https://') ? '_blank' : ''}
              >
                <Button
                  icon={<PlusIcon className="w-16 text-[#333]" />}
                  style={{
                    backgroundColor: cta?.button_background_color?.hex,
                    border: `1px solid ${cta?.button_border_color?.hex}`,
                    color: cta?.button_text_color?.hex,
                  }}
                  className="mt-26 rounded-none px-12 py-12 shadow-sm hover:!bg-[#F4F4F4]"
                >
                  <Typography
                    as="span"
                    className={`text-16 font-medium text-primary-black ${NationalSemibold.className}`}
                  >
                    {cta?.button?.title}
                  </Typography>
                </Button>
              </Link>
            ))}
          </div>
        ))}
      </div>

      {tileContent && <HeaderDropdownTile tileContent={tileContent} />}
    </div>
  );
};

export default MenuDropdown;